import { Fragment, useCallback, useRef } from 'react';
import { FlatList, ScrollView, View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import { Spinner, useTheme } from '@ui-kitten/components';
import lodashIsEmpty from 'lodash/isEmpty';

import EmptyData from '../../Components/EmptyData';
import { textCategory } from '../../Components/Reservation/constants';
import ResponsiveText from '../../Components/ResponsiveText';
import ScreenWrapper from '../../Components/ScreenWrapper';
import SearchReservation from '../../Components/Web/SearchReservation';
import ThemeStyle from '../../Theme/styles';
import BreakPoints from '../../Theme/styles/breakpoints';

import CheckAvailability from '../ReservationEventDetails/components/CheckAvailability';

import ReservationCard from './components/Card';
import EventsFilterSection from './components/EventsFilters';
import ExclusiveBanner from './components/ExclusiveBanner';
import PopularOrFavoriteSection from './components/ListSections/PopularOrFavorite';
import useListing from './controller/useListing';

const Events = () => {
  const theme = useTheme();
  const scrollRef = useRef<ScrollView | FlatList>();
  const yPosOfFirstSection = useRef<number>(0);
  const {
    availabilityRef,
    isListingLoadingMore,
    lastSectionText,
    listingData,
    listingFilter,
    selectedFilter,
    onBookNow,
    onReachedEnd,
  } = useListing();

  const _onSearch = useCallback(() => {
    if ('scrollToOffset' in scrollRef.current) {
      scrollRef.current?.scrollToOffset({
        animated: true,
        offset: yPosOfFirstSection.current,
      });
    }
  }, []);

  const _renderPopulars = useCallback(() => {
    if (listingFilter.reservationType.value !== 'all') return null;
    return (
      <Fragment>
        <PopularOrFavoriteSection onBookNow={onBookNow} favoriteOnly />
        <PopularOrFavoriteSection onBookNow={onBookNow} />
      </Fragment>
    );
  }, [listingFilter.reservationType.value, onBookNow]);

  const _renderEmpty = useCallback(() => {
    return (
      <View style={[ThemeStyle.flex1, ThemeStyle.pageVerticalSpacing]}>
        <EmptyData type="filter" fill={theme['color-info-500']} />
      </View>
    );
  }, [theme]);

  return (
    <ScreenWrapper
      onEndReachedThreshold={0.5}
      onEndReached={onReachedEnd}
      onRef={(r) => (scrollRef.current = r)}
      persistentScrollbar
      showsVerticalScrollIndicator
    >
      <View style={ThemeStyle.flex1}>
        <ExclusiveBanner />

        <SearchReservation size="large" />

        <EventsFilterSection onSearch={_onSearch} />

        <View
          onLayout={(event) => {
            const layout = event.nativeEvent.layout;
            yPosOfFirstSection.current = layout.y;
          }}
        >
          {_renderPopulars()}
        </View>

        <View style={ThemeStyle.pageVerticalSpacing}>
          <View style={ThemeStyle.spacingBottomMedium}>
            <ResponsiveText category={textCategory.HEADING}>
              {lastSectionText}
            </ResponsiveText>
          </View>

          {!lodashIsEmpty(listingData) ? (
            <View
              dataSet={{ media: ids.listSectionContainer }}
              // @ts-ignore
              style={styles.listSectionContainer}
            >
              {listingData?.map((item, i) => (
                <ReservationCard
                  key={`${item?.store?.id}.${i}`}
                  {...item}
                  params={selectedFilter}
                  onBookNow={onBookNow}
                />
              ))}
            </View>
          ) : (
            !isListingLoadingMore && _renderEmpty()
          )}

          {isListingLoadingMore && (
            <View
              style={[
                ThemeStyle.flexDirectionRowCenterCenter,
                ThemeStyle.spacingBottom,
              ]}
            >
              <Spinner size="giant" status="info" />
            </View>
          )}
        </View>

        <CheckAvailability ref={availabilityRef} />
      </View>
    </ScreenWrapper>
  );
};

const { ids, styles } = StyleSheet.create({
  listSectionContainer: {
    //@ts-ignore
    display: 'grid',
    //@ts-ignore
    columnGap: 60,
    rowGap: 60,
    //@ts-ignore
    gridColumnGap: 60,

    [`@media ${BreakPoints.md}`]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    [`@media ${BreakPoints.lg}`]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
    [`@media ${BreakPoints.xxl}`]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
    [`@media ${BreakPoints.xxxl}`]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
  },
});

export default Events;
